import React, { useContext, useEffect, useState } from 'react'
import '../index.scss'
import { observer } from 'mobx-react'
import Button from '@Components/Buttons/Button'
import { ICONS } from '@Const/common'
import Navigation from '@App/navigation'
import { urlHasComponent } from '@App/helpers/url'
import { StoresContext } from '@App/contexts'
import SaveDraftButton from '../../../Buttons/SaveDraftButton'
import SubmitWorkButton from '@Components/Buttons/SubmitWorkButton'
import { WorkModel } from '@Entities/work/work'
import DeleteDraftModal from '@Components/Navigations/CreateWorkBottomNavs/DeleteDraftModal'
import { useNavigate } from 'react-router'
import RootStore from '@App/stores/root-store'
import { CLEAR_WORK_DRAFT } from '@Const/work-registration-forms'
import ConfirmSubmission from '../ConfirmSubmission'
import CopyWorkButton from '@Components/Buttons/CopyWorkButton'

interface FormBottomNavBarProps {
    hideBackLink?: boolean
    backLink?: string
    nextLink?: string
    typeWork?: string
    isFormStep?: boolean
    isSubmittedStep?: boolean
    isFinishEntityStep?: boolean
}

const FormBottomNavBar = ({
    backLink,
    nextLink,
    isFormStep,
    isSubmittedStep,
    isFinishEntityStep,
    typeWork,
}: FormBottomNavBarProps) => {
    const { workStore, userStore, popupStore, rightsholderStore } = useContext<RootStore>(StoresContext)

    const [sidebarIsOpen, setSidebarIsOpen] = useState<boolean>(false)
    const [draftModalOpen, setDraftModalOpen] = useState<boolean>(false)
    const [disableButton, setDisableButton] = useState<boolean>(false)
    const [showSaveDraft, setShowSaveDraft] = useState<boolean>(true)
    const [overclaimConfirmed, setOverclaimConfirmed] = useState<boolean>(false)
    const [submitOverclaimConfirmed, setSubmitOverclaimConfirmed] = useState<boolean>(false)
    const navigate = useNavigate()

    const currentRhId = rightsholderStore.currentRightsholderId
    const isInternal = userStore.isInternal
    const isValidStep = workStore.wizardStepIsValid

    useEffect(() => {
        if (
            isSubmittedStep ||
            !urlHasComponent('create-work') ||
            ((urlHasComponent('draft') || urlHasComponent('bulk')) && urlHasComponent('edit'))
        ) {
            setShowSaveDraft(false)
        }
    }, [isSubmittedStep])

    const createWork = async (isSavingDraftWork: boolean) => {
        const draft = workStore.workDraftLocal
        if (!draft) {
            return
        }
        setDisableButton(true)
        const creatingWork: WorkModel = {
            ...draft,
            id: !isSavingDraftWork ? draft.id : null,
            isComplete: !isSavingDraftWork,
        }
        await workStore.createWorkFromNavBar(creatingWork, currentRhId, !draft.isComplete, isSavingDraftWork)
        setDisableButton(false)
        setSidebarIsOpen(false)
    }

    const deleteDraft = async (draft: WorkModel) => {
        if (draft.id) {
            await workStore.deleteDraftRightsHolder(draft.id)
            setDraftModalOpen(false)
            popupStore.setAlertData(`${draft.title} draft has been deleted`, false)
            workStore.setWorkDraftToLocal(CLEAR_WORK_DRAFT)
            navigate(Navigation.rightholder.registrationDrafts.root(currentRhId as number))
        }
    }

    const displayOverclaimShareWarning =
        workStore.isWorkDraftOverclaim() && workStore.isOverclaimAllowed && !overclaimConfirmed

    const displayOverclaimSubmitWarning =
        workStore.isWorkDraftOverclaim() && workStore.isOverclaimAllowed && !submitOverclaimConfirmed

    const confirmationPopupType = displayOverclaimShareWarning || displayOverclaimSubmitWarning ? 'overclaim' : 'submit'

    const onClickConfirmOverclaim = () => {
        if (overclaimConfirmed) {
            setSubmitOverclaimConfirmed(true)
        } else {
            setOverclaimConfirmed(true)
            setSidebarIsOpen(false)
            nextLink && navigate(nextLink)
        }
    }

    return (
        <div className="next-back-bar-bottom">
            <div className="next-back-bar-bottom-content">
                <Button mainColor className="back" type="secondary" typeLink={!!backLink} linkTo={backLink}>
                    Back
                </Button>
                <div className="right">
                    <SaveDraftButton render={showSaveDraft} disabled={isInternal} onClick={() => createWork(true)} />
                    {isSubmittedStep ? (
                        <>
                            <CopyWorkButton workId={workStore.createdWorkId} />
                            <Button
                                mainColor
                                type="primary"
                                className="next"
                                typeLink
                                linkOnClick={() => workStore.setWorkDraftToLocal(CLEAR_WORK_DRAFT)}
                                linkTo={Navigation.rightholder.registrationHistory.root(currentRhId as number)}
                                disabled={!isValidStep}
                            >
                                Done
                            </Button>
                        </>
                    ) : isFormStep ? (
                        <>
                            {typeWork === 'draft' && (
                                <Button mainColor type="primary" onClick={() => setDraftModalOpen(true)}>
                                    Delete Draft
                                </Button>
                            )}
                            <SubmitWorkButton disabled={!isValidStep} onClick={() => setSidebarIsOpen(true)} />
                        </>
                    ) : displayOverclaimShareWarning ? (
                        <Button
                            mainColor
                            type="primary"
                            icon={isFinishEntityStep ? undefined : ICONS.arrow_forward}
                            className="next"
                            onClick={() => setSidebarIsOpen(true)}
                            disabled={!isValidStep}
                        >
                            Next
                        </Button>
                    ) : (
                        <Button
                            mainColor
                            type="primary"
                            className="next"
                            icon={isFinishEntityStep ? undefined : ICONS.arrow_forward}
                            typeLink={!!nextLink}
                            linkTo={nextLink}
                            disabled={!isValidStep}
                        >
                            {isFinishEntityStep ? 'Done' : 'Next'}
                        </Button>
                    )}
                </div>
            </div>
            <ConfirmSubmission
                isInternal={isInternal}
                disableButton={disableButton}
                isSideBarOpen={sidebarIsOpen}
                closeSidebar={() => setSidebarIsOpen(false)}
                createWork={() => createWork(false)}
                step={confirmationPopupType}
                onClickConfirmOverclaim={onClickConfirmOverclaim}
            />
            <DeleteDraftModal
                open={draftModalOpen}
                closeModal={() => setDraftModalOpen(false)}
                title={workStore.workDraftLocal?.title}
                individual
                handleDeleteDraft={() => workStore.workDraftLocal && deleteDraft(workStore.workDraftLocal)}
                disabled={isInternal}
            />
        </div>
    )
}

export default observer(FormBottomNavBar)
